import { forwardRef, useContext, useState } from "react";
import navStyle from "../styles/nav.module.scss";
import { MaterialContext } from "../context/MaterialContext";
import { UserContext } from "../context/UserContext";
import { isVendorUser } from "../utilities/AuthState";
import {
  DEFAULT_PRICE_MAX,
  DEFAULT_PRICE_MIN,
} from "../utilities/adminSettings";
import { closeIcon } from "./Icons";
import MultiRangeSlider from "./MultRangeSlider";

const FilterBox = forwardRef(function FilterBox(
  { filterMenuIsVisible, setFilterMenuIsVisible },
  ref,
) {
  const {
    orderBy,
    changeOrderBy,
    changePriceRange,
    status,
    changeStatus,
    changeIsFilter,
    isFilter,
    brandArticlesOnly,
    brandArticleFilterAvailable,
    brandArticleFilterLabel,
    changeBrandArticlesOnly,
    priceBounds,
  } = useContext(MaterialContext);
  const { userInformation } = useContext(UserContext);
  const canViewRestrictedStatuses = isVendorUser(userInformation);
  const [reset, setReset] = useState(false);
  const sliderMin = Number.isFinite(priceBounds?.min)
    ? priceBounds.min
    : DEFAULT_PRICE_MIN;
  const sliderMax = Number.isFinite(priceBounds?.max)
    ? priceBounds.max
    : DEFAULT_PRICE_MAX;

  const handleOrder = (value) => {
    changeOrderBy(value);
  };

  const handleChangeStatus = (value) => {
    changeStatus(value);
    changeIsFilter(value !== "commercial" || brandArticlesOnly);
  };

  const handleChangePricing = (value) => {
    changePriceRange(value);
    setReset(false);
    changeIsFilter(true);
  };

  const handleClearFilters = () => {
    changeOrderBy("modified");
    setReset(true);
    changeStatus("commercial");
    changeBrandArticlesOnly(false);
    changePriceRange([{ min: sliderMin, max: sliderMax }]);
    changeIsFilter(false);
    setFilterMenuIsVisible(false);
  };

  const handleBrandArticles = () => {
    const next = !brandArticlesOnly;
    changeBrandArticlesOnly(next);
    if (next) {
      changeIsFilter(true);
    }
  };

  return (
    <div
      ref={ref}
      className={`${navStyle.filterBox} ${
        filterMenuIsVisible ? navStyle.filterBoxOpen : ""
      }`}
      onClick={(event) => event.stopPropagation()}
      role="dialog"
      aria-hidden={!filterMenuIsVisible}
      aria-labelledby="filter-box-title"
    >
      <div className={navStyle.filterTitleRow}>
        <div className={navStyle.filterTitleCopy}>
          <p className={navStyle.filterTitleEyebrow}>Library</p>
          <h2 id="filter-box-title" className={navStyle.filterTitle}>
            Filter viewed materials
          </h2>
        </div>
        <button
          type="button"
          className={navStyle.filterCloseButton}
          onClick={() => setFilterMenuIsVisible(false)}
          aria-label="Close filters"
        >
          {closeIcon}
        </button>
      </div>

      <div className={navStyle.filterPanel}>
        <div className={navStyle.filterSection}>
          <div className={navStyle.filterHeader}>Material order</div>
          <div className={navStyle.filterOptions}>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                orderBy === "modified" ? navStyle.active : ""
              }`}
              onClick={() => handleOrder("modified")}
            >
              Updated
            </button>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                orderBy === "none" ? navStyle.active : ""
              }`}
              onClick={() => handleOrder("none")}
            >
              Added
            </button>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                orderBy === "popular" ? navStyle.active : ""
              }`}
              onClick={() => handleOrder("popular")}
            >
              Popular
            </button>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                orderBy === "alphabetical" ? navStyle.active : ""
              }`}
              onClick={() => handleOrder("alphabetical")}
            >
              Alpha #
            </button>
          </div>
        </div>

        <div className={navStyle.filterSection}>
          <div className={navStyle.filterHeader}>Material status</div>
          <div className={navStyle.filterOptions}>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                status === "commercial" ? navStyle.active : ""
              }`}
              onClick={() => handleChangeStatus("commercial")}
            >
              Commercial
            </button>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                status === "development" ? navStyle.active : ""
              }`}
              onClick={() => handleChangeStatus("development")}
            >
              Development
            </button>
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                status === "concept" ? navStyle.active : ""
              }`}
              onClick={() => handleChangeStatus("concept")}
            >
              Concept
            </button>
            {canViewRestrictedStatuses ? (
              <>
                <button
                  type="button"
                  className={`${navStyle.filterOption} ${
                    status === "hidden" ? navStyle.active : ""
                  }`}
                  onClick={() => handleChangeStatus("hidden")}
                >
                  Hidden
                </button>
                <button
                  type="button"
                  className={`${navStyle.filterOption} ${
                    status === "error" ? navStyle.active : ""
                  }`}
                  onClick={() => handleChangeStatus("error")}
                >
                  Error
                </button>
              </>
            ) : null}
            <button
              type="button"
              className={`${navStyle.filterOption} ${
                status === "none" ? navStyle.active : ""
              }`}
              onClick={() => handleChangeStatus("none")}
            >
              All
            </button>
          </div>
        </div>

        <div className={navStyle.filterSection}>
          <div className={navStyle.filterHeader}>Pricing range</div>
          <MultiRangeSlider
            min={sliderMin}
            max={sliderMax}
            reset={reset}
            onChange={({ min, max }) =>
              handleChangePricing([{ min: min, max: max }])
            }
          />
        </div>

        {brandArticleFilterAvailable ? (
          <div className={navStyle.filterSection}>
            <div className={navStyle.filterHeader}>Brand</div>
            <div className={`${navStyle.filterOptions} ${navStyle.filterOptionsWide}`}>
              <button
                type="button"
                className={`${navStyle.filterOption} ${navStyle.filterOptionWrap} ${
                  brandArticlesOnly ? navStyle.active : ""
                }`}
                aria-pressed={brandArticlesOnly}
                onClick={handleBrandArticles}
              >
                {brandArticleFilterLabel}
              </button>
            </div>
          </div>
        ) : null}

        <div className={navStyle.filterFooter}>
          <button
            type="button"
            className={`${navStyle.filterReset} ${
              isFilter ? "" : navStyle.filterResetHidden
            }`}
            onClick={handleClearFilters}
            disabled={!isFilter}
            aria-hidden={!isFilter}
            tabIndex={isFilter ? 0 : -1}
          >
            Reset filters
          </button>
        </div>
      </div>
    </div>
  );
});

export default FilterBox;
